import { useEffect, useState } from "react";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useModules } from "@/context/ModulesContext";
import { Plus, ChevronLeft, ChevronRight, Trash2, X, MapPin, Users, DollarSign, Clock, SlidersHorizontal, Repeat } from "lucide-react";

const services = ["Lawn Mowing", "Edging & Trimming", "Fertilization", "Leaf Removal", "Aeration", "Landscaping", "Snow Removal", "Cleanup"];
const statusColors = {
  scheduled: "bg-accent/15 border-l-accent text-accent",
  in_progress: "bg-[#E9C46A]/20 border-l-[#E9C46A] text-[#8a6d1f]",
  completed: "bg-primary/10 border-l-primary text-primary",
  cancelled: "bg-muted border-l-muted-foreground text-muted-foreground line-through",
};
const statusBadge = {
  scheduled: "bg-accent/15 text-accent",
  in_progress: "bg-[#E9C46A]/25 text-[#8a6d1f]",
  completed: "bg-primary/10 text-primary",
  cancelled: "bg-muted text-muted-foreground",
};

function startOfWeek(d) {
  const date = new Date(d);
  date.setDate(date.getDate() - date.getDay());
  date.setHours(0, 0, 0, 0);
  return date;
}
const fmt = (d) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};
const jobHours = (j) => {
  if (!j.start_time || !j.end_time) return 0;
  const [sh, sm] = j.start_time.split(":").map(Number);
  const [eh, em] = j.end_time.split(":").map(Number);
  return Math.max(0, (eh * 60 + em - sh * 60 - sm) / 60);
};
const jobLabor = (j) => (j.crew || []).reduce((a, c) => a + (Number(c.hourly_rate) || 0), 0) * jobHours(j);

const empty = {
  title: "", customer_id: "", customer_name: "", service_type: "Lawn Mowing",
  services_list: [],
  assigned_to: "", assigned_to_name: "", scheduled_date: fmt(new Date()),
  start_time: "09:00", end_time: "10:00", status: "scheduled", recurring: "none",
  price: 0, address: "", notes: "", crew: [],
};
const uid = () => Math.random().toString(36).slice(2, 10);

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [team, setTeam] = useState([]);
  const [view, setView] = useState("week"); // day | week | month
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [selectedDay, setSelectedDay] = useState(null); // date string for day-detail dialog
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const { modules, setModules } = useModules();
  const setMod = async (key, val) => {
    const next = { ...modules, [key]: val };
    setModules(next);
    try { await api.put("/settings/modules", { visibility: next }); }
    catch (e) { toast.error("Couldn't save that setting"); }
  };
  const showTimes = modules["schedule.show_times"] === true;
  const fCrew = modules["schedule.field.crew"] !== false;
  const fStatus = modules["schedule.field.status"] !== false;
  const fRecurring = modules["schedule.field.recurring"] !== false;
  const fPrice = modules["schedule.field.price"] !== false;
  const fAddress = modules["schedule.field.address"] !== false;
  const fNotes = modules["schedule.field.notes"] !== false;
  const FIELD_TOGGLES = [
    ["schedule.show_times", "Start / End times", showTimes],
    ["schedule.field.crew", "Crew & labor", fCrew],
    ["schedule.field.status", "Status", fStatus],
    ["schedule.field.recurring", "Recurring", fRecurring],
    ["schedule.field.price", "Price", fPrice],
    ["schedule.field.address", "Address", fAddress],
    ["schedule.field.notes", "Notes", fNotes],
  ];

  const load = () => api.get("/jobs").then((r) => setJobs(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const occurrenceOn = (j, key) => {
    if (j.scheduled_date === key) return "base";
    if (!j.recurring || j.recurring === "none" || j.status === "cancelled") return null;
    if (key <= j.scheduled_date) return null;
    const base = new Date(j.scheduled_date + "T00:00");
    const cur = new Date(key + "T00:00");
    const diffDays = Math.round((cur - base) / 86400000);
    if (j.recurring === "weekly" && diffDays % 7 === 0) return "occ";
    if (j.recurring === "biweekly" && diffDays % 14 === 0) return "occ";
    if (j.recurring === "monthly" && cur.getDate() === base.getDate()) return "occ";
    return null;
  };
  const jobsFor = (key) => {
    const out = [];
    for (const j of jobs) {
      const t = occurrenceOn(j, key);
      if (t === "base") out.push(j);
      else if (t === "occ") out.push({ ...j, scheduled_date: key, status: "scheduled", _occurrence: true, _baseId: j.id });
    }
    return out.sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  };
  const openJob = (j) => openEdit(j._occurrence ? (jobs.find((x) => x.id === j._baseId) || j) : j);

  const shift = (dir) => setAnchor((a) => {
    const n = new Date(a);
    if (view === "day") n.setDate(n.getDate() + dir);
    else if (view === "week") n.setDate(n.getDate() + dir * 7);
    else n.setMonth(n.getMonth() + dir);
    return n;
  });
  const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setAnchor(d); };

  const openNew = (date) => { setForm({ ...empty, services_list: [], scheduled_date: date || fmt(new Date()) }); setEditing(null); setOpen(true); };
  const openEdit = (j) => {
    const services_list = (j.services_list && j.services_list.length)
      ? j.services_list
      : (j.service_type ? [{ id: uid(), name: j.service_type, price: Number(j.price) || 0 }] : []);
    setForm({ ...empty, ...j, services_list });
    setEditing(j.id); setOpen(true);
  };

  const addService = (name) => setForm((f) => ({ ...f, services_list: [...(f.services_list || []), { id: uid(), name: name || "", price: 0 }] }));
  const updateService = (id, key, val) => setForm((f) => ({ ...f, services_list: (f.services_list || []).map((s) => (s.id === id ? { ...s, [key]: val } : s)) }));
  const removeService = (id) => setForm((f) => ({ ...f, services_list: (f.services_list || []).filter((s) => s.id !== id) }));
  const servicesTotal = (form.services_list || []).reduce((a, s) => a + (Number(s.price) || 0), 0);

  const payloadOf = (f) => {
    const list = (f.services_list || []).filter((s) => (s.name || "").trim() || Number(s.price));
    const total = list.reduce((a, s) => a + (Number(s.price) || 0), 0);
    const p = {
      ...f,
      services_list: list.map((s) => ({ ...s, price: Number(s.price) || 0 })),
      price: list.length ? total : (Number(f.price) || 0),
      service_type: list.length ? (list[0].name || f.service_type) : f.service_type,
    };
    delete p.id; delete p.created_at; return p;
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Job title is required");
    try {
      if (editing) await api.put(`/jobs/${editing}`, payloadOf(form));
      else await api.post("/jobs", payloadOf(form));
      toast.success(editing ? "Job updated" : "Job scheduled");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/jobs/${id}`); toast.success("Job removed"); load(); };

  const onCustomer = (id) => {
    const c = customers.find((x) => x.id === id);
    setForm((f) => {
      const next = { ...f, customer_id: id, customer_name: c ? c.name : "" };
      const custAddr = c && (c.address || c.city) ? [c.address, c.city, c.state].filter(Boolean).join(", ") : "";
      if (custAddr && !f.address.trim()) next.address = custAddr;
      if (c && (c.default_services || []).length > 0 && (f.services_list || []).length === 0) {
        next.services_list = c.default_services.map((s) => ({ id: uid(), name: s.name, price: Number(s.price) || 0 }));
        if (!f.title.trim()) next.title = c.default_services.map((s) => s.name).filter(Boolean).join(" + ");
      }
      return next;
    });
  };
  const addCrew = (id) => {
    const s = team.find((x) => x.id === id);
    if (!s || (form.crew || []).some((c) => c.user_id === id)) return;
    const crew = [...(form.crew || []), { user_id: id, name: s.name, hourly_rate: Number(s.hourly_rate) || 0 }];
    setForm((f) => ({ ...f, crew, assigned_to_name: crew.map((c) => c.name).join(", "), assigned_to: crew[0].user_id }));
  };
  const removeCrew = (id) => {
    const crew = (form.crew || []).filter((c) => c.user_id !== id);
    setForm((f) => ({ ...f, crew, assigned_to_name: crew.map((c) => c.name).join(", "), assigned_to: crew[0] ? crew[0].user_id : "" }));
  };

  const laborRate = (form.crew || []).reduce((a, c) => a + (Number(c.hourly_rate) || 0), 0);
  const estHours = jobHours(form);
  const estLabor = laborRate * estHours;

  // ----- header label
  const weekStart = startOfWeek(anchor);
  const weekDays = Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const headerLabel = view === "day"
    ? anchor.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })
    : view === "week"
      ? `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric" })}`
      : anchor.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const todayKey = fmt(new Date());

  // ----- Day view content
  const DayList = ({ dateKey, embedded }) => {
    const list = jobsFor(dateKey);
    const revenue = list.reduce((a, j) => a + (Number(j.price) || 0), 0);
    const labor = list.reduce((a, j) => a + jobLabor(j), 0);
    return (
      <div className="space-y-3" data-testid="day-list">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className="font-semibold" data-testid="day-job-count">{list.length} {list.length === 1 ? "job" : "jobs"}</span>
          <span className="text-muted-foreground flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{money(revenue)} revenue</span>
          <span className="text-muted-foreground flex items-center gap-1"><Users className="h-3.5 w-3.5" />{money(labor)} est. labor</span>
          {!embedded && <Button size="sm" className="ml-auto bg-primary text-primary-foreground hover:bg-[#152e17]" data-testid="day-add-job" onClick={() => openNew(dateKey)}><Plus className="h-4 w-4 mr-1" />Add job</Button>}
        </div>
        {list.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">No jobs scheduled this day.</div>
        ) : list.map((j) => (
          <div key={j.id} data-testid="job-card" onClick={() => openJob(j)}
            className={`cursor-pointer rounded-lg border border-border border-l-4 p-3 hover:shadow-md transition-shadow ${statusColors[j.status] || ""}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-foreground">{j.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">{j.recurring && j.recurring !== "none" && <Repeat className="h-3 w-3 text-primary shrink-0" />}{j.customer_name || j.service_type}</div>
              </div>
              <Badge className={`text-[0.65rem] capitalize shrink-0 ${statusBadge[j.status]}`}>{j.status.replace("_", " ")}</Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              {showTimes && (j.start_time || j.end_time) && <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{j.start_time}{j.end_time ? `–${j.end_time}` : ""}</span>}
              {j.address && <span className="flex items-center gap-1 truncate max-w-[16rem]"><MapPin className="h-3.5 w-3.5" />{j.address}</span>}
              {(j.crew || []).length > 0 && <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{(j.crew || []).map((c) => c.name).join(", ")}</span>}
              {Number(j.price) > 0 && <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{money(j.price)}</span>}
            </div>
            {(j.services_list || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1" data-testid="job-services">
                {(j.services_list || []).map((s) => (
                  <Badge key={s.id} variant="outline" className="text-[0.65rem]">{s.name}{Number(s.price) ? ` · ${money(s.price)}` : ""}</Badge>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  // ----- Month grid
  const monthStart = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const monthGridStart = startOfWeek(monthStart);
  const monthCells = Array.from({ length: 42 }, (_, i) => { const d = new Date(monthGridStart); d.setDate(d.getDate() + i); return d; });

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="prev-btn" onClick={() => shift(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-display font-bold tracking-tight min-w-[12rem] text-center" data-testid="range-label">{headerLabel}</div>
          <Button variant="outline" size="icon" data-testid="next-btn" onClick={() => shift(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={goToday}>Today</Button>
        </div>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" data-testid="schedule-fields-btn"><SlidersHorizontal className="h-4 w-4 mr-1.5" />Fields</Button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <p className="text-sm font-semibold">Job form fields</p>
              <p className="text-xs text-muted-foreground mb-3">Show or hide fields on the scheduling form (applies business-wide).</p>
              <div className="space-y-2.5">
                {FIELD_TOGGLES.map(([key, label, val]) => (
                  <label key={key} className="flex items-center justify-between text-sm cursor-pointer" data-testid={`field-toggle-${key}`}>
                    <span>{label}</span>
                    <Switch checked={val} onCheckedChange={(v) => setMod(key, v)} />
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
          <div className="inline-flex rounded-lg border border-border p-0.5 bg-muted/40">
            {["day", "week", "month"].map((v) => (
              <button key={v} data-testid={`view-${v}`} onClick={() => setView(v)}
                className={`px-3 py-1.5 text-sm font-semibold rounded-md capitalize transition-colors ${view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {v}
              </button>
            ))}
          </div>
          <Button data-testid="add-job-btn" onClick={() => openNew()} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">
            <Plus className="h-4 w-4 mr-1.5" /> Schedule Job
          </Button>
        </div>
      </div>

      {/* DAY VIEW */}
      {view === "day" && (
        <div className="bg-card border border-border rounded-lg p-4" data-testid="day-view">
          <DayList dateKey={fmt(anchor)} />
        </div>
      )}

      {/* WEEK VIEW */}
      {view === "week" && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3" data-testid="week-view">
          {weekDays.map((d) => {
            const key = fmt(d);
            const dayJobs = jobsFor(key);
            const isToday = key === todayKey;
            return (
              <div key={key} data-testid={`day-${key}`} className="bg-card border border-border rounded-lg min-h-[10rem] flex flex-col">
                <button onClick={() => setSelectedDay(key)} data-testid={`day-header-${key}`}
                  className={`px-3 py-2 border-b border-border flex items-center justify-between text-left ${isToday ? "bg-primary text-primary-foreground rounded-t-lg" : "hover:bg-muted"}`}>
                  <div>
                    <div className="text-[0.65rem] uppercase tracking-wide opacity-70">{d.toLocaleDateString("en-US", { weekday: "short" })}</div>
                    <div className="font-display font-bold text-sm">{d.getDate()}</div>
                  </div>
                  <span onClick={(e) => { e.stopPropagation(); openNew(key); }} className="p-1 rounded hover:bg-black/10 transition-colors"><Plus className="h-3.5 w-3.5" /></span>
                </button>
                <div className="p-2 space-y-1.5 flex-1">
                  {dayJobs.map((j) => (
                    <div key={j.id} data-testid="job-card" onClick={() => openJob(j)}
                      className={`cursor-pointer rounded-md border-l-2 px-2 py-1.5 text-xs ${statusColors[j.status]} hover:opacity-90 transition-opacity`}>
                      <div className="font-semibold text-foreground">{j.title}</div>
                      <div className="opacity-80 flex items-center gap-1">{j.recurring && j.recurring !== "none" && <Repeat className="h-3 w-3 shrink-0" />}{showTimes && j.start_time ? `${j.start_time} · ` : ""}{j.customer_name || j.service_type}</div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW */}
      {view === "month" && (
        <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="month-view">
          <div className="grid grid-cols-7 text-center text-[0.65rem] uppercase tracking-wide text-muted-foreground border-b border-border">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((w) => <div key={w} className="py-2 font-semibold">{w}</div>)}
          </div>
          <div className="grid grid-cols-7">
            {monthCells.map((d) => {
              const key = fmt(d);
              const inMonth = d.getMonth() === anchor.getMonth();
              const dayJobs = jobsFor(key);
              const isToday = key === todayKey;
              return (
                <div key={key} data-testid={`day-${key}`} onClick={() => setSelectedDay(key)}
                  className={`group/day text-left min-h-[6.5rem] border-b border-r border-border p-1.5 align-top hover:bg-muted/60 transition-colors cursor-pointer ${inMonth ? "" : "bg-muted/30"}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-semibold h-6 w-6 flex items-center justify-center rounded-full ${isToday ? "bg-primary text-primary-foreground" : inMonth ? "" : "text-muted-foreground"}`}>{d.getDate()}</span>
                    <span className="flex items-center gap-1">
                      {dayJobs.length > 0 && <span className="text-[0.6rem] text-muted-foreground">{dayJobs.length}</span>}
                      <span onClick={(e) => { e.stopPropagation(); openNew(key); }} className="p-0.5 rounded hover:bg-black/10 transition-colors opacity-0 group-hover/day:opacity-100"><Plus className="h-3 w-3" /></span>
                    </span>
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {dayJobs.map((j) => (
                      <div key={j.id} data-testid="job-card" onClick={(e) => { e.stopPropagation(); openJob(j); }}
                        className={`rounded px-1 py-0.5 text-[0.65rem] leading-tight border-l-2 cursor-pointer hover:opacity-90 transition-opacity ${statusColors[j.status]}`}>{j.recurring && j.recurring !== "none" ? "↻ " : ""}{showTimes && j.start_time ? `${j.start_time} ` : ""}{j.customer_name || j.title}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* DAY DETAIL DIALOG */}
      <Dialog open={!!selectedDay} onOpenChange={(v) => !v && setSelectedDay(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="day-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {selectedDay ? new Date(selectedDay + "T00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" }) : ""}
            </DialogTitle>
          </DialogHeader>
          {selectedDay && <DayList dateKey={selectedDay} embedded />}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelectedDay(null)}>Close</Button>
            <Button data-testid="day-detail-add" onClick={() => { const k = selectedDay; setSelectedDay(null); openNew(k); }} className="bg-primary text-primary-foreground hover:bg-[#152e17]"><Plus className="h-4 w-4 mr-1" />Add job this day</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* JOB EDIT DIALOG */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center justify-between">
              {editing ? "Edit Job" : "Schedule Job"}
              {editing && (
                <button data-testid="delete-job-btn" onClick={() => { remove(editing); setOpen(false); }} className="p-1.5 rounded hover:bg-destructive/10 transition-colors duration-200"><Trash2 className="h-4 w-4 text-destructive" /></button>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2 space-y-1.5"><Label>Title *</Label><Input data-testid="job-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Weekly Mow - Smith" /></div>
            <div className="col-span-2 space-y-1.5">
              <Label>Customer</Label>
              <Select value={form.customer_id || "none"} onValueChange={(v) => onCustomer(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="job-customer-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Services (each with its own price)</Label>
              <div className="space-y-2">
                {(form.services_list || []).map((s) => (
                  <div key={s.id} className="flex items-center gap-2" data-testid="service-row">
                    <Input className="flex-1" value={s.name} onChange={(e) => updateService(s.id, "name", e.target.value)} placeholder="Service name" data-testid="service-name-input" />
                    <div className="relative w-28">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                      <Input type="number" className="pl-5" value={s.price} onChange={(e) => updateService(s.id, "price", e.target.value)} data-testid="service-price-input" />
                    </div>
                    <button type="button" onClick={() => removeService(s.id)} data-testid="remove-service-btn" className="p-2 rounded hover:bg-destructive/10 transition-colors"><X className="h-4 w-4 text-destructive" /></button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Select value="" onValueChange={(v) => addService(v)}>
                  <SelectTrigger className="flex-1" data-testid="add-service-select"><SelectValue placeholder="Add a service…" /></SelectTrigger>
                  <SelectContent>{services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Button type="button" variant="outline" size="sm" onClick={() => addService("")} data-testid="add-custom-service-btn"><Plus className="h-4 w-4 mr-1" />Custom</Button>
              </div>
              {(form.services_list || []).length > 0 && (
                <div className="flex justify-between items-center mt-1 rounded-md bg-muted px-3 py-2 text-sm font-semibold" data-testid="services-total">
                  <span>Total</span><span>{money(servicesTotal)}</span>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} /></div>
            {showTimes && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Start</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>End</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
            )}
            {fCrew && (
            <div className="col-span-2 space-y-1.5">
              <Label>Crew (labor cost by hourly rate)</Label>
              <Select value="" onValueChange={addCrew}>
                <SelectTrigger data-testid="job-crew-select"><SelectValue placeholder="Add crew member" /></SelectTrigger>
                <SelectContent>
                  {team.filter((t) => !(form.crew || []).some((c) => c.user_id === t.id)).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name} — ${Number(t.hourly_rate) || 0}/hr</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(form.crew || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(form.crew || []).map((c) => (
                    <Badge key={c.user_id} variant="outline" data-testid="crew-badge" className="gap-1 pr-1">
                      {c.name} · ${c.hourly_rate}/hr
                      <button type="button" onClick={() => removeCrew(c.user_id)} className="rounded hover:bg-destructive/10 p-0.5 transition-colors duration-200"><X className="h-3 w-3" /></button>
                    </Badge>
                  ))}
                </div>
              )}
              {laborRate > 0 && (
                <p className="text-xs text-muted-foreground mt-1" data-testid="est-labor">
                  Crew rate ${laborRate}/hr · Est. {estHours}h · Est. labor {money(estLabor)}
                </p>
              )}
            </div>
            )}
            {fStatus && (
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["scheduled", "in_progress", "completed", "cancelled"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
            {fRecurring && (
            <div className="space-y-1.5">
              <Label>Recurring</Label>
              <Select value={form.recurring} onValueChange={(v) => setForm({ ...form, recurring: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["none", "weekly", "biweekly", "monthly"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
            {fPrice && (
            <div className="space-y-1.5"><Label>Price ($){(form.services_list || []).length > 0 ? " — auto from services" : ""}</Label><Input type="number" data-testid="job-price-input" value={(form.services_list || []).length > 0 ? servicesTotal : form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} readOnly={(form.services_list || []).length > 0} className={(form.services_list || []).length > 0 ? "bg-muted" : ""} /></div>
            )}
            {fAddress && (
            <div className="col-span-2 space-y-1.5"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            )}
            {fNotes && (
            <div className="col-span-2 space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-job-btn" onClick={save} className="bg-primary text-primary-foreground hover:bg-[#152e17] hover:text-white transition-colors duration-200">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
